<template>
  <div class="redbook-launch">
    <div v-if="loading || generating" class="redbook-launch__status">
      {{ generating ? 'AI 文案生成中...' : '加载中...' }}
    </div>
    <div v-else-if="error" class="redbook-launch__status redbook-launch__status--error">{{ error }}</div>
    <div v-else class="redbook-launch__panel">
      <h1 class="redbook-launch__title">{{ detail.activityName }}</h1>
      <p v-if="detail.locationName" class="redbook-launch__loc">{{ detail.locationName }}</p>
      <div v-if="detail.imageUrls && detail.imageUrls.length" class="redbook-launch__imgs">
        <img v-for="(img, i) in detail.imageUrls" :key="'img-' + i" :src="img" alt="">
      </div>
      <p class="redbook-launch__text">{{ contentText }}</p>
      <div v-if="detail.topics && detail.topics.length" class="redbook-launch__topics">
        <span v-for="(t, i) in detail.topics" :key="'t-' + i">#{{ t }}</span>
      </div>
      <el-button type="danger" class="redbook-launch__btn" :loading="publishing" :disabled="!contentText" @click="goXhs(false)">
        {{ publishing ? '正在打开小红书...' : '发布到小红书' }}
      </el-button>
      <p class="redbook-launch__tip">{{ launchTip }}</p>
      <p class="redbook-launch__tip">{{ sdkReadyTip }}</p>
      <p v-if="shareDisableTip" class="redbook-launch__warn">{{ shareDisableTip }}</p>
    </div>
  </div>
</template>

<script>
import { fetchRedbookLaunch, fetchXhsShareConfig, initRedbookPublish, updateRedbookPublishStatus } from '@/api/redbook'
import {
  copyText,
  detectXhsEnv,
  fallbackOpenXhs,
  formatXhsShareError,
  getFallbackLaunchPlan,
  mapShareDisableReason,
  shareViaOfficialSdk
} from '@/utils/xhs-launch'

export default {
  name: 'RedbookLaunch',
  data() {
    const xhsEnv = detectXhsEnv()
    return {
      loading: true,
      generating: false,
      publishing: false,
      error: '',
      detail: {},
      contentText: '',
      recordNo: '',
      clipboardText: '',
      xhsSchemeUrl: 'xhsdiscover://post',
      shareConfigReady: false,
      shareConfigEnabled: false,
      shareDisableTip: '',
      autoLaunchAttempted: false,
      xhsEnv,
      launchTip: xhsEnv.isWeChat
        ? '请使用系统浏览器打开本页，再点击发布（微信内无法直接唤起小红书）。'
        : '文案生成后将自动打开小红书，并带入文案、图片与话题。'
    }
  },
  computed: {
    sdkReadyTip() {
      if (this.shareConfigEnabled) {
        return '已接入小红书官方 SDK，将一键带入文案、图片与话题。'
      }
      if (this.shareConfigReady) {
        return '官方 SDK 暂不可用，将复制文案并尝试打开发笔记页。'
      }
      return '正在检测小红书分享能力...'
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      const activityCode = this.$route.query.activity_code
      const nfcCode = this.$route.query.nfc_code
      if (!activityCode) {
        this.error = '缺少活动参数'
        this.loading = false
        return
      }
      fetchRedbookLaunch({ activity_code: activityCode, nfc_code: nfcCode })
        .then(res => {
          this.detail = res.data || {}
          this.loading = false
          this.generating = true
          return this.initRecord(activityCode, nfcCode)
        })
        .catch(err => {
          this.error = (err && err.message) || '加载失败'
          this.loading = false
          this.generating = false
        })
    },
    initRecord(activityCode, nfcCode) {
      return initRedbookPublish({ activityCode, nfcCode })
        .then(res => {
          const data = res.data || {}
          this.recordNo = data.recordNo
          this.clipboardText = data.clipboardText || ''
          this.xhsSchemeUrl = data.xhsSchemeUrl || 'xhsdiscover://post'
          const payload = data.publishPayload || {}
          this.contentText = payload.contentText || ''
          if (!this.clipboardText) {
            this.clipboardText = this.buildClipboardText(this.contentText, payload.topics || this.detail.topics)
          }
          if (!this.contentText) {
            this.error = '文案生成失败，请重试'
            return
          }
          return this.refreshShareConfig().then(() => {
            if (!this.xhsEnv.isWeChat && this.xhsEnv.isMobile && !this.autoLaunchAttempted) {
              this.autoLaunchAttempted = true
              return this.goXhs(true)
            }
          })
        })
        .catch(err => {
          this.error = (err && err.message) || '文案生成失败'
        })
        .finally(() => {
          this.generating = false
        })
    },
    refreshShareConfig() {
      if (!this.recordNo) {
        return Promise.resolve()
      }
      return fetchXhsShareConfig({ recordNo: this.recordNo })
        .then(res => {
          const shareConfig = (res && res.data) || {}
          this.shareConfigReady = true
          this.shareConfigEnabled = !!shareConfig.enabled
          this.shareDisableTip = shareConfig.enabled ? '' : mapShareDisableReason(shareConfig)
        })
        .catch(err => {
          this.shareConfigReady = true
          this.shareConfigEnabled = false
          this.shareDisableTip = (err && err.message) || '获取小红书分享配置失败'
        })
    },
    buildClipboardText(contentText, topics) {
      const body = String(contentText || '').trim()
      const list = Array.isArray(topics) ? topics.filter(Boolean) : []
      if (!list.length) return body
      const tags = list.map(t => {
        const s = String(t).trim()
        return s.startsWith('#') ? s : '#' + s
      }).join(' ')
      return tags ? body + '\n\n' + tags : body
    },
    reportPublish(resultText, publishStatus) {
      if (!this.recordNo) return Promise.resolve()
      return updateRedbookPublishStatus({
        recordNo: this.recordNo,
        publishStatus: publishStatus || 1,
        xhsInvokeResult: resultText
      }).catch(() => {})
    },
    async goXhs(isAuto) {
      if (this.publishing) return
      if (this.xhsEnv.isWeChat) {
        const copied = await copyText(this.clipboardText)
        this.$message.warning(copied ? '文案已复制，请用系统浏览器打开本页后再发布' : '请长按文案手动复制')
        return
      }
      if (!this.xhsEnv.isMobile) {
        this.$message.warning('请在手机浏览器中打开本页发布到小红书')
        return
      }

      this.publishing = true
      try {
        const configRes = await fetchXhsShareConfig({ recordNo: this.recordNo })
        const shareConfig = (configRes && configRes.data) || {}
        this.shareConfigReady = true
        this.shareConfigEnabled = !!shareConfig.enabled
        this.shareDisableTip = shareConfig.enabled ? '' : mapShareDisableReason(shareConfig)

        if (shareConfig.enabled) {
          await shareViaOfficialSdk(shareConfig)
          await this.reportPublish('xhs_sdk_success', 2)
          if (!isAuto) {
            this.$message.success('已打开小红书，请确认发布')
          }
          return
        }

        const copied = await copyText(this.clipboardText)
        const fallbackPlan = fallbackOpenXhs(this.xhsEnv, this.xhsSchemeUrl)
        const reason = shareConfig.reason || 'sdk_disabled'
        await this.reportPublish(['fallback', reason, copied ? 'clipboard_ok' : 'clipboard_fail'].join('|'), 1)
        if (!isAuto) {
          if (!copied) {
            this.$message.warning('复制失败，请长按上方文案手动复制')
          } else {
            this.$message.success(fallbackPlan.successMsg || getFallbackLaunchPlan(this.xhsEnv).successMsg)
          }
        }
      } catch (err) {
        const copied = await copyText(this.clipboardText)
        fallbackOpenXhs(this.xhsEnv, this.xhsSchemeUrl)
        const msg = formatXhsShareError(err)
        await this.reportPublish(['xhs_sdk_fail', msg, copied ? 'clipboard_ok' : 'clipboard_fail'].join('|'), 3)
        if (!isAuto) {
          this.$message.warning(copied ? ('官方分享失败，已复制文案：' + msg) : ('官方分享失败：' + msg))
        }
      } finally {
        this.publishing = false
      }
    }
  }
}
</script>

<style scoped lang="less">
.redbook-launch {
  min-height: 100vh;
  background: #fff5f5;
  padding: 24px 16px;
  box-sizing: border-box;
}

.redbook-launch__status {
  text-align: center;
  padding-top: 80px;
  color: #606266;

  &--error {
    color: #f56c6c;
  }
}

.redbook-launch__panel {
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  padding: 20px 16px 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.redbook-launch__title {
  margin: 0 0 8px;
  font-size: 20px;
  color: #303133;
}

.redbook-launch__loc {
  margin: 0 0 12px;
  color: #909399;
  font-size: 14px;
}

.redbook-launch__imgs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;

  img {
    width: 96px;
    height: 96px;
    object-fit: cover;
    border-radius: 8px;
  }
}

.redbook-launch__text {
  font-size: 15px;
  line-height: 1.7;
  color: #303133;
  white-space: pre-wrap;
}

.redbook-launch__topics {
  margin: 12px 0 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  span {
    color: #ff2442;
    font-size: 14px;
  }
}

.redbook-launch__btn {
  width: 100%;
}

.redbook-launch__tip {
  margin: 12px 0 0;
  font-size: 12px;
  color: #909399;
  text-align: center;
  line-height: 1.6;
}

.redbook-launch__warn {
  margin: 8px 0 0;
  font-size: 12px;
  color: #e6a23c;
  text-align: center;
  line-height: 1.6;
}
</style>
